/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useByokComplianceSettingWithInitialisation } from '@jira/admin-pages__products-jsm-compliance/src/useByokComplianceSettingWithInitialisation.tsx';
import {
	FREE_EDITION,
	PREMIUM_EDITION,
} from '@jira/platform__application-editions/src/edition.tsx';
import { SERVICE_DESK } from '@jira/platform__application-editions/src/application-key.tsx';
import {
	PERMISSION_STATE,
	usePermissions,
} from '@jira/communications__common/src/controllers/permissions.tsx';
import { FEATURES } from '@jira/communications__common/src/constants/features.tsx';
import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';
import { useOgTenantInfo } from '@jira/eoc__tenant-info/src/controllers/index.tsx';
import { useOpsReportingConfigWithLoadOnMount } from '@jira/router-resources__ops-reports/src/opsReportingConfigResource.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import { useLicensedProducts } from '@atlassian/jira-tenant-context-controller/src/components/licensed-products/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
// eslint-disable-next-line jira/restricted/fedramp-isolated-cloud
import { isIsolatedCloud } from '@atlassian/atl-context';

export const useFeatures = (): {
	hasReports: boolean;
	isOperationReportsEnabled: boolean;
	showCommunicationsMenu: boolean;
	showStatuspageMenu: boolean;
	isStatusPageDraftOnly: boolean;
} => {
	const { isByokCustomer } = useByokComplianceSettingWithInitialisation();
	const isSiteAdmin = useIsSiteAdmin();
	const isAdmin = useIsAdmin();
	const licensedProducts = useLicensedProducts();
	const { shouldUseJSM, loading: ogTenantInfoLoading, isOpsAdmin } = useOgTenantInfo();
	const appEditions = useAppEditions();
	const { hasAccess, isDraftOnly: isStatusPageDraftOnly } = usePermissions(FEATURES.STATUS_PAGES);
	const { data: opsReportingConfig, loading: opsReportingConfigLoading } =
		useOpsReportingConfigWithLoadOnMount();

	const isAdminUser = isAdmin || isOpsAdmin;

	const opsReportsAdminCheck = fg('operations_reports_not_visible_to_ops_admins')
		? isAdminUser
		: isSiteAdmin || isAdmin;

	const hasReports =
		!ogTenantInfoLoading &&
		opsReportsAdminCheck &&
		!isByokCustomer &&
		shouldUseJSM &&
		!!licensedProducts[SERVICE_DESK] &&
		appEditions.serviceDesk !== FREE_EDITION &&
		(fg('use_frontend_feature_encoding_sdk_remaining')
			? isFeatureEnabled('jira', 'jira-oasis-reports')
			: !(fg('hide_reports_for_oasis') && isIsolatedCloud()));

	const isOperationReportsEnabled =
		!opsReportingConfigLoading && Boolean(opsReportingConfig?.isOperationReportsEnabled);

	const tenantContext = useTenantContext();

	const { appPermissions } = tenantContext;
	// Check if user has premium edition
	const isPremium = appEditions?.serviceDesk === PREMIUM_EDITION;

	// Check if user has specific product license
	const isJSMUser = appPermissions.hasServiceDeskAccess;

	const showCommunicationsMenu = isAdminUser && isPremium && isJSMUser;

	const showStatuspageMenu = hasAccess === PERMISSION_STATE.TRUE;

	return {
		hasReports,
		isOperationReportsEnabled,
		showCommunicationsMenu,
		showStatuspageMenu,
		isStatusPageDraftOnly,
	};
};
